import React, { useState } from 'react'
import './order-filter.scss'
import { Row, Column } from '@/components/layout/layout'
import classnames from 'classnames'
import useBemClass from '@/utils/hooks/useBEMClass'
import useSelectList from '@/utils/hooks/useSelectList'
import { useRequest } from 'ahooks'
import { $post } from '@/api'
import { IMallOrder } from '../type'
import { WithInputLimit, INPUT_PRICE_LIMIT_REGEXP } from '@/utils'
import { Spin } from 'antd'
import { mallOrderFilterStore } from './filter-store'
import useSetDataTogether from '@/utils/hooks/useSetDataTogether'
import { layerManage } from '@/utils/layer'
import { Modal } from '@/components/dialog/modal'
import Iconfont from '@/components/iconfont/iconfont'

interface MallOrderFilterProps {}

// 订单状态
const orderStatusList = [
    {
        id: 1,
        name: '待处理',
    },
    {
        id: 2,
        name: '处理中',
    },
    {
        id: 3,
        name: '已完成',
    },
    {
        id: 4,
        name: '退款申请中',
    },
    {
        id: 5,
        name: '待付款',
    },
    {
        id: 6,
        name: '已退款',
    },
    {
        id: 7,
        name: '用户已取消',
    },
    {
        id: 8,
        name: '商家已取消',
    },
    {
        id: 9,
        name: '超时未支付自动取消',
    },
]

// 配送类型
const deliveryTypeList = [
    {
        id: 0,
        name: '自提',
    },
    {
        id: 1,
        name: '配送',
    },
]

// 订单类型
const orderTypeList = [
    {
        id: 0,
        name: '接龙',
    },
    {
        id: 2,
        name: '砍价',
    },
    {
        id: 1,
        name: '微商城',
    },
    {
        id: 3,
        name: '拼团',
    },
    {
        id: 4,
        name: '盲盒',
    },
]

interface ISelectData {
    id: number
    name: string
}

const MallOrderFilter: React.FC<MallOrderFilterProps> = props => {
    const cls = useBemClass('order-mall-filter')

    const selfPickPointReq = useRequest<IMallOrder.SelfPickupSite[]>(
        () =>
            $post({
                url: '/groupon/getselfpickupsites',
            }),
        {
            loadingDelay: 500,
        },
    )

    const setAllData = useSetDataTogether()

    const [max, setMax] = useState<string>(
        mallOrderFilterStore.filterMap.maximum_subtotal ? mallOrderFilterStore.filterMap.maximum_subtotal + '' : '',
    )
    const [min, setMin] = useState<string>(
        mallOrderFilterStore.filterMap.minimum_subtotal ? mallOrderFilterStore.filterMap.minimum_subtotal + '' : '',
    )

    const deliveryTypeSelection = useSelectList<ISelectData>(mallOrderFilterStore.filterMap.delivery_types, 'id')
    const orderTypeSelection = useSelectList<ISelectData>(mallOrderFilterStore.filterMap.order_types, 'id')
    const orderStatusSelection = useSelectList<ISelectData>(mallOrderFilterStore.filterMap.status_list, 'id')
    const pickPointSelection = useSelectList<ISelectData>(mallOrderFilterStore.filterMap.self_pickup_site_ids, 'id')

    const reset = () => {
        setAllData(() => {
            setMax('')
            setMin('')
            deliveryTypeSelection.setSelect([])
            orderTypeSelection.setSelect([])
            orderStatusSelection.setSelect([])
            pickPointSelection.setSelect([])
        })
    }

    return (
        <Modal
            showCancel
            isShow
            width={1000}
            type="success"
            okText="确认"
            cancelText="重置"
            title="筛选"
            onCancel={reset}
            onConfirm={() => {
                mallOrderFilterStore.setFilterMap({
                    delivery_types: deliveryTypeSelection.select,
                    order_types: orderTypeSelection.select,
                    status_list: orderStatusSelection.select,
                    maximum_subtotal: Number(max),
                    minimum_subtotal: Number(min),
                    self_pickup_site_ids: pickPointSelection.select,
                })
                layerManage.pop()
            }}
            onClose={() => {
                layerManage.pop()
            }}
        >
            <Column className={cls('container')}>
                <Spin spinning={selfPickPointReq.loading}>
                    <Row data-align-items-center data-height-50>
                        <div data-color-999 data-font-size-14 data-margin-right-25>
                            订单金额
                        </div>
                        <Row data-align-items-center>
                            {WithInputLimit(
                                <input
                                    value={min}
                                    className={cls('ipt')}
                                    placeholder="最低价"
                                    onChange={e => {
                                        setMin(e.target.value)
                                    }}
                                />,
                                INPUT_PRICE_LIMIT_REGEXP,
                                +max || void 0,
                            )}
                            <div className={cls('line')} />
                            {WithInputLimit(
                                <input
                                    placeholder="最高价"
                                    value={max}
                                    className={cls('ipt')}
                                    onChange={e => {
                                        setMax(e.target.value)
                                    }}
                                />,
                                INPUT_PRICE_LIMIT_REGEXP,
                            )}
                        </Row>
                    </Row>
                    <Row data-align-items-center data-height-50>
                        <div data-color-999 data-font-size-14 data-margin-right-25>
                            收货类型
                        </div>
                        {deliveryTypeList.map(item => (
                            <div
                                className={classnames(cls('tag'), {
                                    [cls('tag', 'active')]: deliveryTypeSelection.has(item),
                                })}
                                key={item.id}
                                onClick={() => {
                                    deliveryTypeSelection.toggle(item)
                                }}
                            >
                                <span>{item.name}</span>
                                {deliveryTypeSelection.has(item) && (
                                    <div className={cls('tag', 'icon')}>
                                        <Iconfont name="iconyixuan" size={14} />
                                    </div>
                                )}
                            </div>
                        ))}
                    </Row>
                    <Row data-align-items-center data-height-50>
                        <div data-color-999 data-font-size-14 data-margin-right-25>
                            订单状态
                        </div>
                        {orderStatusList.map(item => (
                            <div
                                className={classnames(cls('tag'), {
                                    [cls('tag', 'active')]: orderStatusSelection.has(item),
                                })}
                                key={item.id}
                                onClick={() => {
                                    orderStatusSelection.toggle(item)
                                }}
                            >
                                <span>{item.name}</span>
                                {orderStatusSelection.has(item) && (
                                    <div className={cls('tag', 'icon')}>
                                        <Iconfont name="iconyixuan" size={14} />
                                    </div>
                                )}
                            </div>
                        ))}
                    </Row>
                    <Row data-align-items-center data-height-50>
                        <div data-color-999 data-font-size-14 data-margin-right-25>
                            订单类型
                        </div>
                        {orderTypeList.map(item => (
                            <div
                                className={classnames(cls('tag'), {
                                    [cls('tag', 'active')]: orderTypeSelection.has(item),
                                })}
                                key={item.id}
                                onClick={() => {
                                    orderTypeSelection.toggle(item)
                                }}
                            >
                                <span>{item.name}</span>
                                {orderTypeSelection.has(item) && (
                                    <div className={cls('tag', 'icon')}>
                                        <Iconfont name="iconyixuan" size={14} />
                                    </div>
                                )}
                            </div>
                        ))}
                    </Row>
                    {!!selfPickPointReq.data?.length && (
                        <Row data-align-items-center data-height-50>
                            <div data-color-999 data-font-size-14 data-margin-right-25>
                                多自提点
                            </div>
                            {selfPickPointReq.data.map(item => (
                                <div
                                    className={classnames(cls('tag'), {
                                        [cls('tag', 'active')]: pickPointSelection.has(item),
                                    })}
                                    key={item.id}
                                    onClick={() => {
                                        pickPointSelection.toggle(item)
                                    }}
                                >
                                    <span>{item.name}</span>
                                    {pickPointSelection.has(item) && (
                                        <div className={cls('tag', 'icon')}>
                                            <Iconfont name="iconyixuan" size={14} />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </Row>
                    )}
                </Spin>
            </Column>
        </Modal>
    )
}

export default MallOrderFilter
